import React from 'react';
import { cn } from '../../utils/cn';

export interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizes: Record<string, string> = {
  sm: 'h-4 w-4 border-2',
  md: 'h-6 w-6 border-2',
  lg: 'h-9 w-9 border-[3px]',
};

/**
 * Token-colored animated loading ring. Renders `role="status"` with an
 * sr-only label so screen readers announce the loading state; the ring
 * itself is `aria-hidden`.
 */
export const Spinner: React.FC<SpinnerProps> = ({ size = 'md', className }) => (
  <span role="status" className={cn('inline-flex items-center justify-center', className)}>
    <span
      aria-hidden="true"
      className={cn(
        'inline-block animate-spin rounded-full border-solid border-[var(--primary-500)] border-t-transparent',
        sizes[size]
      )}
    />
    <span className="sr-only">Loading</span>
  </span>
);

export interface LoadingStateProps {
  label?: string;
}

/**
 * Centered Spinner + muted label, for full-section/page loading states.
 */
export const LoadingState: React.FC<LoadingStateProps> = ({ label = 'Loading...' }) => (
  <div className="flex flex-col items-center justify-center gap-3 py-10">
    <Spinner size="md" />
    <span data-testid="loading-state-label" className="text-sm text-[var(--muted-foreground)]">
      {label}
    </span>
  </div>
);

export default Spinner;
